import type { DeliveryMethodValue, PickerLabel } from './PeoplePicker.types';
import type { HardcodedLabel } from './usePeoplePickerButtonController';

/**
 * Determines whether a person should be disabled (non-selectable) in the people picker
 * based on the current delivery mode and their available contact info.
 */
export const isPersonDisabled = ({
  hasEmail,
  hasPhone,
  hasGuestKey = true,
  emailOnly,
  smsOnly,
  allowUnreachableSelection,
  enableSmsSelection,
  deliveryMethod,
}: Readonly<{
  hasEmail: boolean;
  hasPhone: boolean;
  hasGuestKey?: boolean;
  emailOnly: boolean;
  smsOnly: boolean;
  allowUnreachableSelection: boolean;
  enableSmsSelection: boolean;
  deliveryMethod?: DeliveryMethodValue;
}>): boolean => {
  if (!hasGuestKey) return true;
  if (emailOnly) return !hasEmail;
  if (smsOnly) return !hasPhone;
  if (allowUnreachableSelection) return false;
  if (!enableSmsSelection) return !hasEmail;

  switch (deliveryMethod) {
    case 'text':
      return !hasPhone;
    case 'email':
      return !hasEmail;
    default:
      return !(hasEmail || hasPhone);
  }
};

/**
 * Returns a sublabel describing missing contact info, or undefined when
 * the SMS delivery-method selector handles the sublabel instead.
 */
export const getSublabelText = ({
  smsOnly,
  emailOnly,
  hasPhone,
  enableSmsSelection,
  hasEmail,
  tq,
}: Readonly<{
  smsOnly: boolean;
  emailOnly: boolean;
  hasPhone: boolean;
  enableSmsSelection: boolean;
  hasEmail: boolean;
  tq: (strings: TemplateStringsArray, ...values: unknown[]) => string;
}>): string | undefined => {
  if (emailOnly) return hasEmail ? '' : tq`Missing email address`;
  if (smsOnly) return hasPhone ? '' : tq`Missing phone number`;
  if (enableSmsSelection) return undefined;
  return hasEmail ? '' : tq`Missing email address`;
};

/**
 * Resolves the effective delivery method from the channel mode flags.
 */
export const resolveDeliveryMethod = ({
  smsOnly,
  emailOnly,
  initialDeliveryMethod,
}: Readonly<{
  smsOnly: boolean;
  emailOnly: boolean;
  initialDeliveryMethod?: DeliveryMethodValue;
}>): DeliveryMethodValue => {
  if (smsOnly) return 'text';
  if (emailOnly) return 'email';
  return initialDeliveryMethod ?? 'both';
};

/**
 * Merges `hardcodedLabels` into the event's own tags for `requiredScope` mode. Each tag is
 * intersected with the scope up front, which is what lets the reducer keep a plain union over the
 * selected tags and still land on `scope AND (tag OR tag …)`, and makes each tag's count the number
 * of people selecting it would actually reach.
 *
 * An empty scope is treated as *no* scope. Intersecting against it would gut every tag to zero and
 * flag every hand-pick as outside the scope — the state a wedding reaches once everyone has
 * responded, and the one a general event starts in, since the legacy attendance questions the scope
 * is built from don't exist there. Falling back to the plain additive list leaves the scope label
 * as an explanatory disabled row and keeps the tags usable.
 *
 * Shared by both picker controllers: they feed the same dialog and must stay behaviourally
 * identical, so the merge lives here rather than being mirrored in each.
 */
export const scopeUserLabels = (hardcodedLabels: ReadonlyArray<HardcodedLabel>, userLabels: ReadonlyArray<HardcodedLabel>): ReadonlyArray<PickerLabel> => {
  const scopeIds = new Set(hardcodedLabels.flatMap(label => label.people.map(person => person.id)));
  if (scopeIds.size === 0) return [...hardcodedLabels, ...userLabels];

  // Tags whose intersection is empty stay in the list but render disabled — dropping them entirely
  // would re-create the original complaint ("my tag isn't there") for tags nobody in scope carries.
  const scopedUserLabels = userLabels.map(label => ({ ...label, people: label.people.filter(person => scopeIds.has(person.id)) }));
  return [...hardcodedLabels.map(label => ({ ...label, isRequiredScope: true })), ...scopedUserLabels];
};
